import React, { FC } from 'react';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import { Box, TransparentButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import InvoicePausedIcon from '~Assets/icons/invoice_paused.svg';
import NavigateLeftIcon from '~/Assets/icons/navigate-left.svg';
import { useButton, useI18n, useSelector } from '~Hooks';
import { useHistory } from 'react-router-dom';

const HandyWrapper = styled(Box)`
  width: 100vw;
  height: 100%;
  background: var(--colorBlueGray1200);
  position: relative;
`;

const ModalWrapper = styled(Box)`
  position: relative;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  animation: fadeIn 1s ease;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InfoBox = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 16px;
`;

const BackButton = styled(TransparentButton)`
  position: absolute;
  top: 16px;
  left: 16px;
  color: var(--colorBlueGray800);
  font-size: 16px;
  text-transform: none;
`;

const BackButtonIcon = styled.div`
  width: 32px;
  height: 32px;
  mask-image: url(${NavigateLeftIcon});
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: var(--colorBlueGray800);
  margin-right: 8px;
`;

interface Props {
  /**
   * FROZEN: The Venue's billing account has been frozen due to (for instance) unpaid invoices.
   * EXPIRED: The Venue's billing account has expired because their subscription has ended and needs to be renewed.
   */
  // type: 'FROZEN' | 'EXPIRED'; // TODO: EXPIRED has not been implemented yet
  daysOverdue?: number;
  isHandy?: boolean;
  isStripeLicense?: boolean; // Whether the access block is due to an unpaid Stripe license, used to determine the text in the modal
}

/** Modal component that will be displayed if the Venue's billing account has been freezed due to for instance unpaid invoices */
const AccessBlocked: FC<Props> = ({ daysOverdue, isHandy, isStripeLicense }) => {
  const { i18n } = useI18n();
  const history = useHistory();

  const venueId = useSelector((state) => state.venueId);

  const backButton = useButton({
    onClick: () => {
      history.push(`/venue/${venueId}/gateway`);
    },
  });

  const getContent = () => (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="xs"
        justifyContent="center">
        <Icon
          height={isHandy ? '48px' : '64px'}
          width={isHandy ? '48px' : '64px'}
          src={InvoicePausedIcon}
          color="var(--colorBlueGray200)"
        />

        <Heading level={isHandy ? 2 : 1} color="var(--colorWhite)">
          {i18n('AccessBlocked.Frozen.Title')}
          {/* TODO: i18n('AccessBlocked.Expired.Title')} */}
        </Heading>
        <Paragraph level={isHandy ? 4 : 2} color="var(--colorBlueGray200)">
          {isStripeLicense
            ? i18n('AccessBlocked.Frozen.Desc.Stripe')
            : i18n('AccessBlocked.Frozen.Desc')}
          {/* TODO: i18n('AccessBlocked.Expired.Desc')} */}
        </Paragraph>
      </Box>

      {daysOverdue && (
        <InfoBox fluid display="flex" alignItems="center" justifyContent="center">
          <Box display="flex" flexDirection="column" gap="xs" alignItems="center">
            <Paragraph level={5} color="var(--colorBlueGray200)">
              {i18n('AccessBlocked.DaysOverDue')}
            </Paragraph>
            <Heading level={5} color="var(--colorWhite)">
              {`${daysOverdue} ${i18n('AccessBlocked.DaysOverDue.Days', 45)}`}
            </Heading>
          </Box>
        </InfoBox>
      )}

      <Paragraph level={isHandy ? 5 : 3} color="var(--colorBlueGray200)">
        {i18n('AccessBlocked.ContactManager')}
      </Paragraph>
    </>
  );

  /** If handy - don't display it in a modal */
  if (isHandy) {
    return (
      <HandyWrapper
        fluid
        display="flex"
        flexDirection="column"
        gap="small"
        alignItems="center"
        justifyContent="center"
        padding="small">
        {getContent()}
      </HandyWrapper>
    );
  }

  return (
    <Modal isShowing hide={() => {}} backdropClose={false}>
      <ModalWrapper
        display="flex"
        flexDirection="column"
        gap="medium"
        alignItems="center"
        justifyContent="center"
        padding="3xl"
        marginTop="xs"
        marginBottom="xs">
        <BackButton display="flex" alignItems="center" {...backButton}>
          <BackButtonIcon />
          {i18n('General.GoBack')}
        </BackButton>
        {getContent()}
      </ModalWrapper>
    </Modal>
  );
};

export default AccessBlocked;
